/**
 * <s-product-carousel> — PDP media carousel.
 *
 * The track is a native scroll-snap container, so swipe, trackpad and keyboard
 * scrolling work without this script; it only wires the arrow and thumbnail
 * commands and keeps the active thumbnail and counter in sync.
 *
 * Required markup:
 *   [data-carousel-track]          — horizontal scroll-snap container; its children are slides.
 *   button[command="--prev|--next"] with commandfor = this element's id.
 *   button[command="--go"][data-index] thumbnails with commandfor = this element's id.
 *   [data-carousel-current]        — optional element showing the 1-based active slide number.
 */
class SProductCarousel extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #index = 0;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children);

    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });

    this.#observer = new IntersectionObserver(this.#handleIntersect, { root: this.#track, threshold: 0.6 });
    for (const slide of this.#slides) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleCommand = (event) => {
    const { command, source } = event;
    if (command === '--prev') this.#goTo(this.#index - 1);
    else if (command === '--next') this.#goTo(this.#index + 1);
    else if (command === '--go') this.#goTo(Number(source?.dataset.index ?? 0));
  };

  #goTo(target) {
    const count = this.#slides.length;
    if (count === 0) return;
    const index = (target + count) % count;
    const slide = this.#slides[index];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track horizontally; never move the page or steal focus.
    this.#track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#setActive(this.#slides.indexOf(entry.target));
      }
    }
  };

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    for (const thumb of this.querySelectorAll('button[command="--go"]')) {
      const isActive = Number(thumb.dataset.index) === index;
      thumb.toggleAttribute('data-active', isActive);
      if (isActive) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
    const current = this.querySelector('[data-carousel-current]');
    if (current) current.textContent = String(index + 1);
  }
}

if (!customElements.get('s-product-carousel')) {
  customElements.define('s-product-carousel', SProductCarousel);
}
